import { ListItemButton, ListItemIcon } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import { memo, MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowSmallDown, ArrowSmallRight } from '@/assets/icons';
import { PropertiesTitle } from '@/modules/common/components/PropertiesTitle';
import { AreaItem } from '@/modules/layers/components';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { templatesToFlatCollection } from '@/modules/floorplanDesign/helpers/helpers';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

type GroupItemProps = {
  id: string;
};

type GroupedShape = {
  id: string;
  name: string;
};

const GroupItemComponent = ({ id }: GroupItemProps) => {
  const { t } = useTranslation();
  const getGroupById = useFloorplanDesignStore((state) => state.getGroupById);
  const getAreasInGroup = useFloorplanDesignStore((state) => state.getAreasInGroup);
  const logicalSpaces = useFloorplanDesignStore((state) => state.design.logicalSpaces);
  const obstacles = useFloorplanDesignStore((state) => state.design.obstacles);
  const uxGroups = useFloorplanDesignStore((state) => state.uxonly.groups);
  const templates = useFloorplanDesignStore((state) => state.design.templates);
  const mode = useAppStore((state) => state.mode);
  const selectedIds = useAppStore((state) => state.selectedIds);

  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const [open, setOpen] = useState(false);
  const otherGroupNames = useRef<{ synced: boolean; values: Set<string> }>({
    synced: false,
    values: new Set(),
  });
  const [nameError, setNameError] = useState<string | null>(null);

  const group = getGroupById(id);
  const name = useMemo(() => group?.name ?? '', [group]);
  const selected = useMemo(() => selectedIds.includes(id), [id, selectedIds]);

  const groupedShapes = useMemo((): GroupedShape[] => {
    if (!group) return [];

    const groupedAreaIds = new Set(group.areaIds);
    const groupedAreas = getAreasInGroup(id) ?? [];
    const groupedLogicalSpaces = logicalSpaces.filter((logicalSpace) =>
      groupedAreaIds.has(logicalSpace.id),
    );
    const groupedObstacles = obstacles.filter((obstacle) =>
      groupedAreaIds.has(obstacle.id),
    );

    return [...groupedAreas, ...groupedLogicalSpaces, ...groupedObstacles];
  }, [getAreasInGroup, group, id, logicalSpaces, obstacles]);

  const groupNameChangeHandler = useCallback(
    (value: string) => {
      if (!group) return;

      const sanitizedValue = value.trim();
      const nameIsTaken = otherGroupNames.current.values.has(sanitizedValue);

      if (nameIsTaken) {
        setNameError(t('errors:shape_group.name.duplicated'));
        return;
      }

      setNameError(null);

      fddmSvc.updateGroup({
        ...group,
        name: sanitizedValue,
      });
    },
    [fddmSvc, group, t],
  );

  const populateOtherGroupNames = useCallback(() => {
    otherGroupNames.current = {
      synced: true,
      values: new Set([
        ...uxGroups.filter((uxGroup) => uxGroup.id !== id).map((uxGroup) => uxGroup.name),
        ...templatesToFlatCollection(templates)
          .filter((template) => template.group.id !== id)
          .map((template) => template.group.name),
      ]),
    };
  }, [id, templates, uxGroups]);

  const handleClick = useCallback(() => {
    setOpen((currentOpen) => !currentOpen);
  }, []);

  const onNameClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      if (!otherGroupNames.current.synced) {
        populateOtherGroupNames();
      }
    },
    [populateOtherGroupNames],
  );

  const onBlur = useCallback(() => {
    setNameError(null);
    otherGroupNames.current.synced = false;
  }, []);

  const containerStyle = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'start',
      gap: 1,

      height: 'auto',
      paddingRight: '0px',
      backgroundColor: selected ? 'neutral.grey' : 'shades.light',

      '&:hover': {
        backgroundColor: selected ? 'neutral.grey' : 'shades.light',
        border: 0,
      },
      '&:focus-within': {
        backgroundColor: selected ? 'neutral.grey' : 'initial',
      },
    }),
    [selected],
  );

  const disabled = mode !== 'EDITABLE';

  return (
    <>
      <ListItemButton
        alignItems='flex-start'
        disableRipple
        sx={containerStyle}
        onClick={handleClick}
      >
        <ListItemIcon
          sx={{
            minWidth: (theme) => theme.spacing(3),
            fontSize: 24,
            marginTop: '0.5rem',
          }}
        >
          {open ? <ArrowSmallDown /> : <ArrowSmallRight />}
        </ListItemIcon>

        <PropertiesTitle
          value={name}
          editable={!disabled}
          onClick={onNameClick}
          onChange={groupNameChangeHandler}
          onBlur={onBlur}
          multiline
          fontSize={14}
          fontWeight={400}
          maxRowsPassive={2}
          maxRowsActive={3}
          sx={{ flex: '1 1 auto', marginTop: '4px', marginBottom: '3px' }}
          error={!!nameError}
          helperText={nameError ?? undefined}
        />
      </ListItemButton>

      <Collapse in={open} timeout='auto' unmountOnExit>
        <List component='div' disablePadding>
          {groupedShapes.map((shape) => (
            <AreaItem
              key={shape.id}
              canBeLocked={false}
              canBeHidden={false}
              canBeSelected={false}
              label={shape.name}
              id={shape.id}
              level={0}
              disabled={disabled}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

export const GroupItem = memo(GroupItemComponent);
